/**
 * @description Coordinates requests around an access token refresh. Requests
 * that already took a 401 are parked with `postpone` and sent again by
 * `replay`; requests not yet sent wait on `getPending()` so they leave with
 * the new token rather than earning a 401 of their own.
 *
 * Lives apart from `./axios` so it can be tested on its own — importing that
 * module registers the interceptors, and its refresh branch is unreachable
 * under NODE_ENV=test.
 */
export const createTokenRefresh = () => {
  let subscribers = [];
  let running = null;
  let active = false;

  return {
    /**
     * @description The wait for a usable token, or null once there is one. It
     * never rejects, so a waiting request goes out either way and takes its own
     * 401 if the refresh failed.
     * @returns {Promise|null}
     */
    getPending() {
      return running;
    },

    /**
     * @description Whether a refresh cycle is still in progress — true from
     * `run()` until `replay()` or `rejectAll()` ends it. This outlasts
     * `getPending()`, which clears as soon as the token is usable while the
     * caller may still be holding the parked requests back (see the
     * server-propagation pad in core/axios). Anything deciding whether to
     * *start* a refresh, or whether parking will be answered, has to ask this
     * rather than `getPending()` — otherwise a 401 arriving in that gap starts
     * a second, redundant refresh, or parks a caller nothing will release.
     * @returns {boolean}
     */
    isActive() {
      return active;
    },

    /**
     * @description Start a refresh cycle. Callers must check `isActive()` first
     * — this does not guard against a second concurrent call.
     * @param refresh () => Promise resolving once the new token is usable
     * @returns {Promise} the real outcome, which can reject
     */
    run(refresh) {
      active = true;

      // Called straight away, not on a later tick: `running` must be set
      // before anything can await it.
      let outcome;
      try {
        outcome = Promise.resolve(refresh());
      } catch (error) {
        outcome = Promise.reject(error);
      }

      running = outcome.catch(() => {});
      running.then(() => {
        running = null;
      });

      return outcome;
    },

    /**
     * @description Park a request that came back 401 until the refresh ends.
     * @param request axios request config
     * @param resolve the parked caller's resolve
     */
    postpone(request, resolve) {
      subscribers.push({ request, resolve });
    },

    /**
     * @description Empty the queue and send every parked request again. Each
     * caller is resolved with the retry itself, not with its value, so a caller
     * settles with whatever the retry settles with. Ends the cycle.
     * @param send (request) => Promise
     */
    replay(send) {
      const parked = subscribers;
      subscribers = [];
      active = false;

      parked.forEach(({ request, resolve }) => resolve(send(request)));
    },

    /**
     * @description Empty the queue and fail every parked caller. Used when the
     * refresh itself fails: without it those callers wait forever, and the
     * component that started them never leaves its loading state. Ends the
     * cycle.
     * @param error
     */
    rejectAll(error) {
      const parked = subscribers;
      subscribers = [];
      active = false;

      parked.forEach(({ resolve }) => resolve(Promise.reject(error)));
    },

    /**
     * @description How many retries the next replay will send.
     * @returns {number}
     */
    getSize() {
      return subscribers.length;
    },
  };
};

export default createTokenRefresh();
